import type { RouterInputs } from '@klear/ui/lib/trpc'
import type { Payable } from '@klear/payable/payable'
import {
  payableFormToCreateWire,
  payableFormToUpdateWire,
  payableToForm,
  type PayableForm,
  type PayableFormDirty
} from '@klear/payable/wire.client'
import { PAYABLE_PUBLISH_ACTION, type PayablePublishAction } from '@klear/payable/constants'

export type PayableCommitInput = RouterInputs['payable']['commit']
export type PayableCreateData = ReturnType<typeof payableFormToCreateWire>
export type PayableUpdateData = ReturnType<typeof payableFormToUpdateWire>

export function payableToFormValues(record: Payable): PayableForm {
  return payableToForm(record)
}

export function formValuesToCreateData(values: PayableForm): PayableCreateData {
  return payableFormToCreateWire(values)
}

export function formValuesToUpdateData(values: PayableForm, dirty: PayableFormDirty): PayableUpdateData {
  return payableFormToUpdateWire(values, dirty)
}

function formWithCommitAction(
  form: PayableForm,
  publishAction: PayablePublishAction,
  effectiveDate: string | null
): PayableForm {
  return { ...form, publishAction, effectiveDate }
}

function updateDirtyForCommit(dirty: PayableFormDirty): PayableFormDirty {
  return {
    ...dirty,
    publishAction: true,
    effectiveDate: true
  }
}

export function formValuesToCreateCommitInput(
  form: PayableForm,
  traderId: number,
  publishAction: PayablePublishAction,
  effectiveDate: string | null
): PayableCommitInput {
  return {
    traderId,
    data: formValuesToCreateData(formWithCommitAction(form, publishAction, effectiveDate))
  }
}

export function formValuesToUpdateCommitInput(
  form: PayableForm,
  dirty: PayableFormDirty,
  traderId: number,
  payableId: number,
  publishAction: PayablePublishAction,
  effectiveDate: string | null
): PayableCommitInput {
  return {
    id: payableId,
    traderId,
    data: formValuesToUpdateData(formWithCommitAction(form, publishAction, effectiveDate), updateDirtyForCommit(dirty))
  }
}

export function formValuesToCommitInput(
  form: PayableForm,
  traderId: number,
  opts?: {
    payableId?: number
    dirty?: PayableFormDirty
    publishAction?: PayablePublishAction
    effectiveDate?: string | null
  }
): PayableCommitInput {
  const publishAction = opts?.publishAction ?? form.publishAction ?? PAYABLE_PUBLISH_ACTION.PUBLISH
  const effectiveDate = opts?.effectiveDate ?? form.effectiveDate

  if (opts?.payableId != null && opts.payableId > 0) {
    return formValuesToUpdateCommitInput(form, opts.dirty ?? {}, traderId, opts.payableId, publishAction, effectiveDate)
  }

  return formValuesToCreateCommitInput(form, traderId, publishAction, effectiveDate)
}

export function commitInputForSave(
  form: PayableForm,
  traderId: number,
  opts?: { payableId?: number; dirty?: PayableFormDirty }
): PayableCommitInput {
  return formValuesToCommitInput(form, traderId, {
    payableId: opts?.payableId,
    dirty: opts?.dirty,
    publishAction: PAYABLE_PUBLISH_ACTION.SAVE,
    effectiveDate: null
  })
}

export function commitInputForPublish(
  form: PayableForm,
  traderId: number,
  opts?: { payableId?: number; dirty?: PayableFormDirty }
): PayableCommitInput {
  return formValuesToCommitInput(form, traderId, {
    payableId: opts?.payableId,
    dirty: opts?.dirty,
    publishAction: PAYABLE_PUBLISH_ACTION.PUBLISH,
    effectiveDate: null
  })
}

export function commitInputForComplete(
  form: PayableForm,
  traderId: number,
  opts?: { payableId?: number; dirty?: PayableFormDirty }
): PayableCommitInput {
  return formValuesToCommitInput(form, traderId, {
    payableId: opts?.payableId,
    dirty: opts?.dirty,
    publishAction: PAYABLE_PUBLISH_ACTION.COMPLETE,
    effectiveDate: null
  })
}
